import { useCallback, useEffect, useMemo, useState } from 'react';

import { AppError } from '../../api/errors.js';
import { DATASET_CREATE_DATA_TYPES } from '../../constants/index.js';
import type {
  DatasetCreateDataType,
  DatasetOwnershipFilter,
} from '../../constants/index.js';
import { useDataHubClient } from '../../context/DataHubClientContext.js';
import type { Dataset } from '../../services/dataHubService.interface.js';
import { DataHubService } from '../../services/dataHubService.js';

import { filterDatasets } from './datasetFilter.js';
import type {
  UseDatasetsParams,
  UseDatasetsResult,
} from './useDatasets.interface.js';

// Page size for the server-side ownership-scoped fetch. 100 is the API max, so
// the full set loads in the fewest round-trips.
const FETCH_PAGE_SIZE = 100;
// Safety cap on the number of pages walked, guarding against a runaway loop if
// the ownership scope ever grows unexpectedly large.
const MAX_PAGES = 50;

const DEFAULT_ACCESS_FILTER = 'All' as const;
const DEFAULT_DATA_TYPE = 'All types' as const;
const DEFAULT_OWNERSHIP_FILTER: DatasetOwnershipFilter = 'All Datasets';

interface DatasetScopeResult {
  datasets: Dataset[];
  totalCount: number;
}

function toOwnerObjectId(
  filter?: DatasetOwnershipFilter,
  userObjectId?: string | null,
): string | undefined {
  if (filter === 'My Datasets' && userObjectId) return userObjectId;
  return undefined;
}

export function useDatasets(params: UseDatasetsParams = {}): UseDatasetsResult {
  const client = useDataHubClient();
  const service = useMemo(() => new DataHubService(client), [client]);

  // Full ownership-scoped set fetched from the server. Text/access/type
  // filtering happens client-side against this list.
  const [allDatasets, setAllDatasets] = useState<Dataset[]>([]);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorStatusCode, setErrorStatusCode] = useState<number | undefined>(
    undefined,
  );
  const [refreshCount, setRefreshCount] = useState(0);

  const refresh = useCallback(() => {
    setRefreshCount((n) => n + 1);
  }, []);

  const query = params.query ?? '';
  const accessFilter = params.accessFilter ?? DEFAULT_ACCESS_FILTER;
  const dataType = params.dataType ?? DEFAULT_DATA_TYPE;
  const ownerUserObjectId = toOwnerObjectId(
    params.ownershipFilter ?? DEFAULT_OWNERSHIP_FILTER,
    params.userObjectId,
  );
  const fetchByDataType = params.fetchByDataType ?? false;

  // Fetch the entire ownership-scoped set whenever ownership changes or a
  // refresh is requested. Search, access, and type filters are client-side, so
  // they do NOT re-trigger a fetch.
  useEffect(() => {
    let cancelled = false;

    if (params.enabled === false) {
      setAllDatasets([]);
      setTotalCount(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);
    setErrorStatusCode(undefined);
    setAllDatasets([]);
    setTotalCount(null);

    void (async () => {
      try {
        async function fetchScope(
          scopedDataType?: DatasetCreateDataType,
        ): Promise<DatasetScopeResult> {
          const collected: Dataset[] = [];
          let continuationToken: string | undefined;
          let reportedTotal: number | null = null;
          let page = 0;

          do {
            const result = await service.searchDatasets({
              ownerUserObjectId,
              ...(scopedDataType !== undefined
                ? { dataType: scopedDataType }
                : {}),
              pageSize: FETCH_PAGE_SIZE,
              continuationToken,
              includeCount: page === 0,
            });

            collected.push(...result.items);
            if (page === 0) reportedTotal = result.totalCount;
            continuationToken = result.continuationToken ?? undefined;
            page += 1;
          } while (continuationToken && page < MAX_PAGES);

          return {
            datasets: collected,
            totalCount: reportedTotal ?? collected.length,
          };
        }

        const baseline = await fetchScope();
        if (cancelled) return;

        let collected = baseline.datasets;
        if (fetchByDataType) {
          const typedResults = await Promise.allSettled(
            DATASET_CREATE_DATA_TYPES.map(async (dataType) => ({
              dataType,
              scope: await fetchScope(dataType),
            })),
          );
          if (cancelled) return;

          const successfulTypes = new Set<string>();
          const typedDatasets = typedResults.flatMap((result) => {
            if (result.status === 'rejected') return [];
            successfulTypes.add(result.value.dataType);
            return result.value.scope.datasets;
          });
          const fallbackDatasets = baseline.datasets.filter(
            (dataset) =>
              dataset.dataType === undefined ||
              !successfulTypes.has(dataset.dataType),
          );
          collected = Array.from(
            new Map(
              [...typedDatasets, ...fallbackDatasets].map((dataset) => [
                dataset.id,
                dataset,
              ]),
            ).values(),
          );
        }

        setAllDatasets(collected);
        setTotalCount(fetchByDataType ? collected.length : baseline.totalCount);
        setIsLoading(false);
      } catch (err: unknown) {
        if (cancelled) return;
        setError(
          err instanceof Error ? err.message : 'Failed to load datasets',
        );
        setErrorStatusCode(
          err instanceof AppError ? err.statusCode : undefined,
        );
        setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    service,
    ownerUserObjectId,
    fetchByDataType,
    params.enabled,
    refreshCount,
  ]);

  const datasets = useMemo(
    () => filterDatasets(allDatasets, query, accessFilter, dataType),
    [allDatasets, query, accessFilter, dataType],
  );

  return {
    datasets,
    totalCount,
    isLoading,
    error,
    errorStatusCode,
    // Everything is loaded up front, so there is no incremental paging.
    isLoadingMore: false,
    hasMore: false,
    loadMore: () => undefined,
    refresh,
  };
}
